// src/components/date/GranularityDateSelector.tsx

import { DatePicker } from '@mui/x-date-pickers/DatePicker'
import { DateTimePicker } from '@mui/x-date-pickers/DateTimePicker'
import dayjs, { Dayjs } from 'dayjs'

import { limitNowAsMax } from '@/api'
import { FolderContractView, FolderId } from '@/types/domain/folder'

import { useFolderContract } from '../../hooks'

const autoHeightSlotProps = {
  textField: { variant: 'standard' as const },
  desktopPaper: {
    sx: {
      '& .MuiDateCalendar-root': {
        height: 'auto !important',
        maxHeight: 'unset !important',
      },
      '& .MuiDayCalendar-slideTransition': {
        minHeight: 'unset !important',
      },
    },
  },
}

type GranularityDateSelectorProps = {
  granularity: string
  id: FolderId
  label: string
  onChange: (date: string) => void
  value: string
  mode: 'from' | 'to'
}

export const GranularityDateSelector = ({
  granularity,
  id,
  label,
  onChange,
  value,
  mode,
}: GranularityDateSelectorProps) => {
  const cData: FolderContractView = useFolderContract(id)
  if (!cData) return null
  const cFrom = cData.date_begin
  const cTo = cData.date_expire

  const boundaries = (d: string) => {
    const date = dayjs(d)

    return date.isBefore(cFrom) ? cFrom : date.isAfter(cTo) ? cTo : d
  }

  const sanitize = (date: string) =>
    limitNowAsMax(boundaries(date), granularity, mode)

  const handleChange = (v: Dayjs | null) => {
    if (!v) return

    let adjusted = v

    if (granularity === 'y')
      adjusted = mode === 'from' ? v.startOf('month') : v.endOf('month')

    if (granularity === 'yy')
      adjusted = mode === 'from' ? v.startOf('year') : v.endOf('year')

    const iso = adjusted.format('YYYY-MM-DDTHH:mm:ss')

    onChange(sanitize(iso))
  }

  const pickerValue = value ? dayjs(value) : null

  // hourly
  if (granularity === 'd') {
    return (
      <DateTimePicker
        ampm={false}
        // use :00 for minutes KAR-2381
        format={'DD.MM.YYYY HH:00'}
        label={label}
        maxDateTime={dayjs(cTo)}
        minDateTime={dayjs(cFrom)}
        onChange={handleChange}
        slotProps={{ textField: { variant: 'standard' } }}
        value={pickerValue}
      />
    )
  }

  // daily
  if (granularity === 'm') {
    return (
      <DatePicker
        format={'DD.MM.YYYY'}
        label={label}
        maxDate={dayjs(cTo)}
        minDate={dayjs(cFrom)}
        onChange={handleChange}
        slotProps={{ textField: { variant: 'standard' } }}
        value={pickerValue}
      />
    )
  }

  // monthly
  if (granularity === 'y') {
    return (
      <DatePicker
        format={'MMMM YYYY'}
        label={label}
        maxDate={dayjs(cTo)}
        minDate={dayjs(cFrom)}
        onChange={handleChange}
        openTo={'month'}
        slotProps={autoHeightSlotProps}
        value={pickerValue}
        views={['year', 'month']}
      />
    )
  }

  // yearly
  return (
    <DatePicker
      format={'YYYY'}
      label={label}
      maxDate={dayjs(cTo)}
      minDate={dayjs(cFrom)}
      onChange={handleChange}
      openTo={'year'}
      slotProps={autoHeightSlotProps}
      value={pickerValue}
      views={['year']}
    />
  )
}
